// MaterialUpload Component
// ========================
// Drag-and-drop file upload for journey materials (PDF, DOCX, TXT)
// Feature #80 - Material upload component for learning plan workflow

import React, { useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { JourneyMaterial } from '../../types';

interface MaterialUploadProps {
  journeyId: string;
  materials: JourneyMaterial[];
  uploading: boolean;
  onUpload: (file: File) => Promise<{ success: boolean; error?: string }>;
  onDelete: (materialId: string) => Promise<{ success: boolean; error?: string }>;
  disabled?: boolean;
}

// Format file size for display
const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Get icon for file type
const getFileIcon = (fileType: string): string => {
  switch (fileType.toLowerCase()) {
    case 'pdf':
      return '📕';
    case 'docx':
    case 'doc':
      return '📘';
    case 'txt':
      return '📄';
    default:
      return '📎';
  }
};

const MaterialUpload: React.FC<MaterialUploadProps> = ({
  materials,
  uploading,
  onUpload,
  onDelete,
  disabled = false,
}) => {
  const { t } = useTranslation(['library']);
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Supported file types
  const supportedTypes = ['pdf', 'docx', 'txt'];
  const acceptedMimeTypes = '.pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain';

  // Validate file
  const validateFile = (file: File): string | null => {
    const extension = file.name.split('.').pop()?.toLowerCase();

    if (!extension || !supportedTypes.includes(extension)) {
      return t('library:materialUpload.validation.invalidFormat', { formats: supportedTypes.join(', ').toUpperCase() });
    }

    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return t('library:materialUpload.validation.fileTooLarge');
    }

    return null;
  };

  // Handle file selection
  const handleFiles = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0 || disabled) return;

    setError(null);

    // Process first file only
    const file = files[0];
    const validationError = validateFile(file);

    if (validationError) {
      setError(validationError);
      return;
    }

    const result = await onUpload(file);
    if (!result.success) {
      setError(result.error || t('library:materialUpload.validation.uploadFailed'));
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onUpload, disabled]);

  // Drag and drop handlers
  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  };

  // Handle delete
  const handleDelete = async (materialId: string) => {
    setDeletingId(materialId);
    setError(null);

    const result = await onDelete(materialId);
    if (!result.success) {
      setError(result.error || t('library:materialUpload.validation.deleteFailed'));
    }

    setDeletingId(null);
  };

  return (
    <div className="space-y-4">
      {/* Error message */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <p className="text-red-600 dark:text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Drop zone */}
      <div
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
        className={`
          relative p-6 border-2 border-dashed rounded-xl transition-all cursor-pointer
          ${isDragging
            ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-primary-400 dark:hover:border-primary-500'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={(e) => handleFiles(e.target.files)}
          accept={acceptedMimeTypes}
          className="hidden"
          disabled={disabled}
        />

        <div className="flex flex-col items-center gap-3 text-center">
          {uploading ? (
            <>
              <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <svg
                  className="animate-spin h-6 w-6 text-primary-600 dark:text-primary-400"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <p className="text-sm text-neutral-medium">{t('library:materialUpload.uploading')}</p>
            </>
          ) : (
            <>
              <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                <span className="text-2xl">📤</span>
              </div>
              <div>
                <p className="font-medium text-neutral-dark dark:text-white">
                  {isDragging ? t('library:materialUpload.dropHere') : t('library:materialUpload.dragOrClick')}
                </p>
                <p className="text-sm text-neutral-medium mt-1">
                  {t('library:materialUpload.supportedFormats')}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Uploaded materials list */}
      {materials.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-neutral-dark dark:text-white">
            {t('library:materialUpload.uploadedMaterials', { count: materials.length })}
          </h4>
          <div className="space-y-2">
            {materials.map((material) => (
              <div
                key={material.id}
                className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-sm transition-shadow"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="text-xl flex-shrink-0">{getFileIcon(material.fileType)}</span>
                  <div className="min-w-0">
                    <p className="font-medium text-neutral-dark dark:text-white truncate">
                      {material.fileName}
                    </p>
                    <p className="text-xs text-neutral-medium">
                      {formatFileSize(material.fileSize)}
                      {material.extractedText && (
                        <span className="ml-2 text-green-600 dark:text-green-400">
                          ✓ {t('library:materialUpload.textExtracted')}
                        </span>
                      )}
                    </p>
                  </div>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(material.id);
                  }}
                  disabled={deletingId === material.id}
                  className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors flex-shrink-0 disabled:opacity-50"
                  title={t('library:materialUpload.deleteMaterial')}
                >
                  {deletingId === material.id ? (
                    <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                  )}
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Empty state with helpful guidance - Feature #239 */}
      {materials.length === 0 && !uploading && (
        <div className="text-center py-4 space-y-2">
          <p className="text-sm text-neutral-medium">
            {t('library:materialUpload.emptyState.description')}
          </p>
          <p className="text-xs text-neutral-medium/70">
            {t('library:materialUpload.emptyState.helpText')}
          </p>
        </div>
      )}
    </div>
  );
};

export default MaterialUpload;
